import { useState } from "react";
import { CheckCircle, Circle, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";

const VIC_CHECKLIST_ITEMS = [
  {
    id: "enrolled-vic",
    title: "Check your VIC enrolment",
    desc: "VIC rolls close 15 days before polling day — November 13, 2026. Verify at vec.vic.gov.au",
    link: "https://www.vec.vic.gov.au",
    linkText: "Check now →",
    urgency: "high",
  },
  {
    id: "electorate-vic",
    title: "Know your VIC electorate",
    desc: "Find your Legislative Assembly district — this determines your local candidates and preferences order.",
    link: "https://www.vec.vic.gov.au",
    linkText: "Find electorate →",
    urgency: "medium",
  },
  {
    id: "voting-system-vic",
    title: "Understand VIC voting",
    desc: "Lower house: number ALL boxes. Upper house (8 regions): vote for at least 5 groups above the line OR 12 candidates below. Both are compulsory.",
    link: "/how-to-vote",
    linkText: "Voting guide →",
    internal: true,
    urgency: "medium",
  },
  {
    id: "policies-vic",
    title: "Read the VIC party policies",
    desc: "Know where Labor, Liberal, Nationals, Greens and Teals stand on state debt, the Suburban Rail Loop, housing and healthcare.",
    link: null,
    urgency: "medium",
  },
  {
    id: "polling-place-vic",
    title: "Find your VIC polling place",
    desc: "Pre-poll opens November 16. Election day is November 28, 8am–6pm AEDT. Find your nearest voting centre at vec.vic.gov.au",
    link: "https://www.vec.vic.gov.au",
    linkText: "Find polling place →",
    urgency: "low",
  },
];

export function VICChecklistWidget() {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [checked, setChecked] = useState({});

  const toggle = (id) => setChecked((prev) => ({ ...prev, [id]: !prev[id] }));
  const doneCount = Object.values(checked).filter(Boolean).length;
  const total = VIC_CHECKLIST_ITEMS.length;
  const pct = Math.round((doneCount / total) * 100);

  const surface   = isDark ? "#1E293B" : "#FFFFFF";
  const textColor = isDark ? "#F8FAFC" : "#0F172A";
  const textSec   = isDark ? "#94A3B8" : "#475569";
  const borderC   = isDark ? "rgba(51,65,85,0.55)" : "rgba(226,232,240,0.85)";
  const shadowMd  = isDark ? "0 4px 20px -2px rgba(0,0,0,0.35)" : "0 4px 16px -2px rgba(15,23,42,0.07)";
  const subBg     = isDark ? "rgba(16,185,129,0.06)" : "rgba(13,92,58,0.04)";
  const urgencyColor = { high: "#F43F5E", medium: "#EAB308", low: "#10B981" };

  return (
    <section data-testid="vic-election-checklist-section">
      <div className="flex items-center gap-2.5 mb-6">
        <div
          className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: "rgba(16,185,129,0.12)", border: "1px solid rgba(16,185,129,0.22)" }}
        >
          <CheckCircle style={{ width: 18, height: 18, color: "#10B981" }} />
        </div>
        <div>
          <h2 className="text-2xl font-bold tracking-tight" style={{ color: textColor, fontFamily: "Outfit, sans-serif" }}>
            Am I Ready to Vote?
          </h2>
          <p className="text-sm" style={{ color: textSec }}>VIC 2026 pre-election readiness checklist</p>
        </div>
      </div>

      {/* Progress */}
      <div
        className="p-4 rounded-xl mb-5"
        style={{ background: subBg, border: `1px solid ${isDark ? "rgba(16,185,129,0.15)" : "rgba(13,92,58,0.10)"}` }}
      >
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold" style={{ color: textColor }}>
            {doneCount === total ? "You're ready to vote!" : `${doneCount} of ${total} complete`}
          </span>
          <span className="text-sm font-bold" style={{ color: doneCount === total ? "#10B981" : textSec }}>{pct}%</span>
        </div>
        <div className="w-full h-2 rounded-full overflow-hidden" style={{ background: isDark ? "rgba(51,65,85,0.5)" : "#E2E8F0" }}>
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${pct}%`, background: "linear-gradient(90deg, #0D5C3A, #10B981)" }}
          />
        </div>
      </div>

      {/* Items */}
      <div className="space-y-3">
        {VIC_CHECKLIST_ITEMS.map((item) => {
          const done = !!checked[item.id];
          return (
            <div
              key={item.id}
              className="transition-all duration-200"
              style={{
                background: done ? (isDark ? "rgba(16,185,129,0.06)" : "rgba(13,92,58,0.04)") : surface,
                border: `1px solid ${done ? (isDark ? "rgba(16,185,129,0.18)" : "rgba(13,92,58,0.12)") : borderC}`,
                borderRadius: 12,
                boxShadow: done ? "none" : shadowMd,
                padding: "14px 16px",
              }}
              data-testid={`vic-checklist-item-${item.id}`}
            >
              <div className="flex items-start gap-3">
                <button
                  onClick={() => toggle(item.id)}
                  className="flex-shrink-0 mt-0.5 transition-all duration-200"
                  style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
                >
                  {done
                    ? <CheckCircle style={{ width: 20, height: 20, color: "#10B981" }} />
                    : <Circle style={{ width: 20, height: 20, color: isDark ? "#475569" : "#CBD5E1" }} />
                  }
                </button>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                    <span
                      className="text-sm font-semibold"
                      style={{
                        color: done ? (isDark ? "#34d399" : "#0D5C3A") : textColor,
                        textDecoration: done ? "line-through" : "none",
                        fontFamily: "Outfit, sans-serif",
                      }}
                    >
                      {item.title}
                    </span>
                    <span style={{
                      fontSize: 10, fontWeight: 600, padding: "1px 7px", borderRadius: 99,
                      background: `${urgencyColor[item.urgency]}14`,
                      color: urgencyColor[item.urgency],
                      border: `1px solid ${urgencyColor[item.urgency]}28`,
                    }}>{item.urgency}</span>
                  </div>
                  <p className="text-xs leading-relaxed" style={{ color: textSec }}>{item.desc}</p>
                  {item.link && (
                    item.internal ? (
                      <button
                        onClick={() => navigate(item.link)}
                        className="text-xs font-semibold mt-2 transition-all duration-150"
                        style={{ color: isDark ? "#34d399" : "#0D5C3A", background: "none", border: "none", cursor: "pointer", padding: 0 }}
                      >
                        {item.linkText}
                      </button>
                    ) : (
                      <a
                        href={item.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs font-semibold mt-2 inline-flex items-center gap-1"
                        style={{ color: isDark ? "#34d399" : "#0D5C3A" }}
                      >
                        {item.linkText} <ChevronRight style={{ width: 11, height: 11 }} />
                      </a>
                    )
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
